/** XeAgents.ai - Contact Placeholder Helper for Flow Builder **/
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { User, Copy, Check, Info } from "lucide-react";

// Contact placeholders available in both manual entry and CSV uploads
// These are the standard fields that are ALWAYS available
export const CONTACT_PLACEHOLDERS = [
  {
    placeholder: "{{first_name}}",
    label: "First Name",
    description: "Contact's first name",
    example: "John",
  },
  {
    placeholder: "{{last_name}}",
    label: "Last Name",
    description: "Contact's last name",
    example: "Smith",
  },
  {
    placeholder: "{{contact_name}}",
    label: "Full Name",
    description: "Contact's full name (first + last)",
    example: "John Smith",
  },
  {
    placeholder: "{{name}}",
    label: "Name",
    description: "Alias for full name",
    example: "John Smith",
  },
  {
    placeholder: "{{phone}}",
    label: "Phone",
    description: "Contact's phone number",
    example: "+1234567890",
  },
  {
    placeholder: "{{email}}",
    label: "Email",
    description: "Contact's email address",
    example: "john@example.com",
  },
] as const;

interface PlaceholderHelperProps {
  onInsert?: (placeholder: string) => void;
  compact?: boolean;
}

export function PlaceholderHelper({ onInsert, compact = false }: PlaceholderHelperProps) {
  const [copiedPlaceholder, setCopiedPlaceholder] = useState<string | null>(null);

  const handleCopy = (placeholder: string) => {
    navigator.clipboard.writeText(placeholder);
    setCopiedPlaceholder(placeholder);
    setTimeout(() => setCopiedPlaceholder(null), 2000);
  };

  const handleInsert = (placeholder: string) => {
    if (onInsert) {
      onInsert(placeholder);
    } else {
      handleCopy(placeholder);
    }
  };

  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
          >
            <User className="w-3 h-3" />
            Personalize
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Contact Placeholders</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Click to insert. These work with both CSV imports and manual entries.
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {CONTACT_PLACEHOLDERS.map((item) => (
                <TooltipProvider key={item.placeholder}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7 text-xs justify-start font-mono"
                        onClick={() => handleInsert(item.placeholder)}
                      >
                        {copiedPlaceholder === item.placeholder ? (
                          <Check className="w-3 h-3 mr-1 text-green-500" />
                        ) : null}
                        {item.label}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <div className="space-y-1">
                        <p className="font-mono text-xs">{item.placeholder}</p>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                        <p className="text-xs">Example: <span className="font-medium">{item.example}</span></p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-primary" />
        <span className="font-medium text-sm">Contact Placeholders</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">
                Use these placeholders to personalize your message. They will be replaced
                with actual contact data from CSV imports or manual entries during the call.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <p className="text-xs text-muted-foreground">
        Click to {onInsert ? "insert" : "copy"}. Works with CSV imports and manual entries.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {CONTACT_PLACEHOLDERS.map((item) => (
          <TooltipProvider key={item.placeholder}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className="cursor-pointer hover:bg-secondary/80 transition-colors font-mono text-xs py-1 px-2"
                  onClick={() => handleInsert(item.placeholder)}
                >
                  {copiedPlaceholder === item.placeholder ? (
                    <Check className="w-3 h-3 mr-1 text-green-500" />
                  ) : (
                    <Copy className="w-3 h-3 mr-1 opacity-50" />
                  )}
                  {item.placeholder}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-medium text-xs">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                  <p className="text-xs">Example: <span className="font-medium">{item.example}</span></p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
}

// Inline hint text for text areas - use this for a minimal footprint
export function PlaceholderHint() {
  return (
    <p className="text-xs text-muted-foreground mt-1">
      <span className="font-medium">Tip:</span> Use{" "}
      <code className="bg-muted px-1 py-0.5 rounded text-[10px]">{"{{first_name}}"}</code>,{" "}
      <code className="bg-muted px-1 py-0.5 rounded text-[10px]">{"{{contact_name}}"}</code>,{" "}
      <code className="bg-muted px-1 py-0.5 rounded text-[10px]">{"{{email}}"}</code>{" "}
      for personalization.
    </p>
  );
}
